<template>
  <q-dialog :model-value="modelValue" maximized transition-show="slide-up" transition-hide="slide-down"
    @update:model-value="$emit('update:modelValue', $event)">
    <q-card class="fact-history-card">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          {{ data.title }}
          <span v-if="stornoMode" class="text-deep-orange text-subtitle2 q-ml-sm">(режим корректировки / сторно)</span>
        </div>
        <q-space />
        <q-btn icon="close" flat round dense v-close-popup />
      </q-card-section>

      <q-card-section v-if="stornoMode" class="q-pt-xs q-pb-none">
        <q-banner dense rounded class="bg-deep-orange-1 text-deep-orange-9" style="font-size:13px">
          <template v-slot:avatar><q-icon name="info" color="deep-orange" /></template>
          Корректировка по датам: измените значение и нажмите «Сохранить» напротив нужной строки
        </q-banner>
      </q-card-section>

      <q-card-section class="fact-history-summary">
        <span class="fact-history-chip">
          <b>Факт:</b> {{ data.factPct }}%
        </span>
        <span class="fact-history-chip">
          <b>СМР:</b> {{ fmtKzt(fhTotalSmrFiltered) }} ₸
        </span>
        <span class="fact-history-chip">
          <b>ТМЦ:</b> {{ fmtKzt(fhTotalTmcFiltered) }} ₸
        </span>
        <span class="fact-history-chip fact-history-chip--count">
          {{ fhFilteredRows.length }} записей
        </span>
      </q-card-section>

      <q-card-section class="fh-filters q-pt-none q-pb-sm">
        <q-select
          v-model="data.filterType"
          :options="fhTypeOptions"
          label="Тип проекта"
          emit-value map-options clearable dense outlined
          style="min-width:220px"
        />
        <q-select
          v-model="data.filterBlock"
          :options="fhBlockOptions"
          label="Блок"
          emit-value map-options clearable dense outlined
          style="min-width:150px"
        />
        <q-input
          v-model="data.filterWork"
          label="Поиск по работе / группе"
          dense outlined clearable
          style="min-width:220px"
        >
          <template v-slot:prepend><q-icon name="search" size="18px" /></template>
        </q-input>
      </q-card-section>

      <q-card-section class="q-pt-none" style="flex:1;overflow:auto">
        <q-inner-loading :showing="data.loading" />
        <template v-if="!data.loading && fhFilteredRows.length">
          <div v-for="group in fhGroupedRows" :key="group.label" class="fh-group-block">
            <div class="fh-group-header">
              <span class="fh-group-title">{{ group.label }}</span>
              <span class="fh-group-totals">
                СМР: {{ fmtKzt(group.smr) }} ₸ &nbsp;|&nbsp; ТМЦ: {{ fmtKzt(group.tmc) }} ₸
                &nbsp;|&nbsp; {{ group.rows.length }} зап.
              </span>
            </div>
            <table class="fact-history-table">
              <colgroup>
                <col v-if="stornoMode" class="fh-col-expand" />
                <col class="fh-col-date" />
                <col v-if="!['work','group','floor'].includes(data.nodeType)" class="fh-col-block" />
                <col v-if="!['work','group'].includes(data.nodeType)" class="fh-col-floor" />
                <col v-if="data.nodeType !== 'work'" class="fh-col-group" />
                <col class="fh-col-work" />
                <col v-if="!stornoMode" class="fh-col-guid" />
                <col class="fh-col-vol" />
                <template v-if="stornoMode">
                  <col class="fh-col-plan" />
                  <col class="fh-col-storno-new" />
                  <col class="fh-col-storno-diff" />
                  <col class="fh-col-storno-act" />
                </template>
                <template v-else>
                  <col class="fh-col-smr" />
                  <col class="fh-col-tmc" />
                </template>
                <col class="fh-col-sent" />
              </colgroup>
              <thead>
                <tr>
                  <th v-if="stornoMode"></th>
                  <th>Дата</th>
                  <th v-if="!['work','group','floor'].includes(data.nodeType)">Блок</th>
                  <th v-if="!['work','group'].includes(data.nodeType)">Этаж</th>
                  <th v-if="data.nodeType !== 'work'">Группа</th>
                  <th>Работа</th>
                  <th v-if="!stornoMode">GUID УСП</th>
                  <th class="text-right">Текущий факт</th>
                  <template v-if="stornoMode">
                    <th class="text-right">Объём</th>
                    <th class="text-right">Новый факт</th>
                    <th class="text-right">Разница</th>
                    <th class="text-center"></th>
                  </template>
                  <template v-else>
                    <th class="text-right">СМР (₸)</th>
                    <th class="text-right">ТМЦ (₸)</th>
                  </template>
                  <th class="text-center">Отпр.</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(r, i) in group.rows" :key="i">
                <tr :class="{ 'fh-row-unsent': !r.isSent }">
                  <td v-if="stornoMode" class="fh-expand-cell">
                    <q-btn
                      v-if="r.uspFactId"
                      flat dense round size="xs"
                      :icon="r.materialsOpen ? 'expand_more' : 'chevron_right'"
                      @click="toggleMaterials(r)"
                    >
                      <q-tooltip>{{ r.materialsOpen ? 'Скрыть материалы' : 'Показать материалы' }}</q-tooltip>
                    </q-btn>
                  </td>
                  <td>{{ r.date }}</td>
                  <td v-if="!['work','group','floor'].includes(data.nodeType)" :title="r.block">{{ r.block }}</td>
                  <td v-if="!['work','group'].includes(data.nodeType)">{{ r.floor }}</td>
                  <td v-if="data.nodeType !== 'work'" :title="r.groupName">{{ r.groupName }}</td>
                  <td :title="r.workName">{{ r.workName }}</td>
                  <td v-if="!stornoMode" class="fh-cell-guid">
                    <template v-if="r.workGuid">
                      <span class="fh-guid-text">{{ r.workGuid }}</span>
                      <q-btn flat dense round size="xs" icon="content_copy" class="fh-guid-copy"
                        @click.stop="copyToClipboard(r.workGuid)">
                        <q-tooltip>Копировать GUID</q-tooltip>
                      </q-btn>
                    </template>
                    <span v-else class="text-grey-5">—</span>
                  </td>
                  <td class="text-right">{{ fmtNum(r.factValue) }}</td>
                  <template v-if="stornoMode">
                    <td class="text-right text-grey-7">{{ fmtNum(r.workSetAmount) }}</td>
                    <td class="fh-storno-cell">
                      <q-input
                        v-if="r.uspFactId"
                        v-model="r.newFactValue"
                        type="number" min="0"
                        dense outlined
                        :disable="r.saving"
                        class="fh-storno-input"
                        @update:model-value="onNewFactInput(r)"
                      />
                      <span v-else class="text-grey-5">—</span>
                    </td>
                    <td class="text-right" :class="{
                      'text-red': r.stornoDiff < 0,
                      'text-green-8': r.stornoDiff > 0,
                    }">
                      <template v-if="r.uspFactId">
                        {{ r.stornoDiff > 0 ? '+' : '' }}{{ round6(r.stornoDiff) }}
                      </template>
                      <span v-else class="text-grey-5">—</span>
                    </td>
                    <td class="text-center">
                      <q-btn
                        v-if="r.uspFactId && r.stornoDiff !== 0"
                        dense flat round size="sm"
                        icon="save"
                        :color="r.isSent ? 'deep-orange' : 'primary'"
                        :loading="r.saving"
                        @click="$emit('save-history-storno', r)"
                      >
                        <q-tooltip>{{ r.isSent ? 'Сохранить корректировку' : 'Применить изменения (факт не отправлен)' }}</q-tooltip>
                      </q-btn>
                    </td>
                  </template>
                  <template v-else>
                    <td class="text-right">{{ fmtKzt(r.smr) }} ₸</td>
                    <td class="text-right">{{ fmtKzt(r.tmc) }} ₸</td>
                  </template>
                  <td class="text-center">
                    <q-icon v-if="r.isSent" name="check_circle" color="green-7" size="16px" />
                    <q-icon v-else name="schedule" color="grey-5" size="16px" />
                  </td>
                </tr>
                <tr v-if="stornoMode && r.materialsOpen" class="fh-mats-row" :class="{ 'fh-row-unsent': !r.isSent }">
                  <td :colspan="matsColspan" class="fh-mats-cell">
                    <q-linear-progress v-if="r.materialsLoading" indeterminate />
                    <template v-else-if="r.workMaterials && r.workMaterials.length">
                      <table class="fh-mats-table">
                        <thead>
                          <tr>
                            <th>Материал</th>
                            <th>Ед.</th>
                            <th class="text-right">Было</th>
                            <th class="text-right">Стало</th>
                            <th class="text-right">Разница</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(m, j) in r.workMaterials" :key="m.material_id || j">
                            <td :title="m.name">{{ m.name }}</td>
                            <td class="text-grey-7">{{ m.unit || '' }}</td>
                            <td class="text-right text-grey-7">
                              {{ fmtNum(getMatOriginal(r, m)) }}
                            </td>
                            <td class="fh-mat-new-cell">
                              <q-input
                                :model-value="getMatNew(r, m)"
                                type="number" min="0"
                                dense outlined
                                :disable="r.saving"
                                class="fh-storno-input"
                                @update:model-value="onMatInput(r, m, $event)"
                              />
                            </td>
                            <td class="text-right" :class="{
                              'text-red': getMatDiff(r, m) < 0,
                              'text-green-8': getMatDiff(r, m) > 0,
                            }">
                              <template v-if="getMatDiff(r, m) !== 0">
                                {{ getMatDiff(r, m) > 0 ? '+' : '' }}{{ round6(getMatDiff(r, m)) }}
                              </template>
                              <span v-else class="text-grey-5">—</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </template>
                    <div v-else-if="r.materialsLoaded" class="text-grey-6 text-center q-pa-sm">
                      По этому факту нет сохранённых материалов
                    </div>
                  </td>
                </tr>
                </template>
              </tbody>
            </table>
          </div>
        </template>
        <div v-else-if="!data.loading" class="text-grey-6 text-center q-pa-lg">
          Нет введённых данных
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { useFormatters } from '../composables/useFormatters';
import { recalcStornoMaterialsByFact } from '../composables/useStornoEditing';

export default {
  name: 'PpmFactHistoryDialog',
  props: {
    modelValue: Boolean,
    data: { type: Object, required: true },
    stornoMode: { type: Boolean, default: false },
    exceedAllowed: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'save-history-storno', 'load-history-materials', 'history-material-input'],
  setup() {
    const { fmtKzt, fmtNum, fmtDateRu } = useFormatters();
    return { fmtKzt, fmtNum, fmtDateRu };
  },
  computed: {
    fhTypeOptions() {
      const set = new Set();
      for (const r of this.data.rows) {
        if (r.projectType) set.add(r.projectType);
      }
      return [...set].sort().map(v => ({ label: v, value: v }));
    },
    fhBlockOptions() {
      const set = new Set();
      for (const r of this.data.rows) {
        if (r.block) set.add(r.block);
      }
      return [...set].sort().map(v => ({ label: v, value: v }));
    },
    fhFilteredRows() {
      const dlg = this.data;
      let rows = dlg.rows;
      if (dlg.filterType) {
        rows = rows.filter(r => r.projectType === dlg.filterType);
      }
      if (dlg.filterBlock) {
        rows = rows.filter(r => r.block === dlg.filterBlock);
      }
      if (dlg.filterWork) {
        const q = dlg.filterWork.toLowerCase();
        rows = rows.filter(r =>
          (r.workName || '').toLowerCase().includes(q) ||
          (r.groupName || '').toLowerCase().includes(q)
        );
      }
      return rows;
    },
    fhGroupedRows() {
      const groups = {};
      for (const r of this.fhFilteredRows) {
        const key = r.projectType || '—';
        if (!groups[key]) groups[key] = { label: key, rows: [], smr: 0, tmc: 0 };
        groups[key].rows.push(r);
        groups[key].smr += r.smr;
        groups[key].tmc += r.tmc;
      }
      return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label));
    },
    fhTotalSmrFiltered() {
      return this.fhFilteredRows.reduce((s, r) => s + r.smr, 0);
    },
    fhTotalTmcFiltered() {
      return this.fhFilteredRows.reduce((s, r) => s + r.tmc, 0);
    },
    matsColspan() {
      const nt = this.data.nodeType;
      let n = 1 + 1 + 1;
      if (!['work','group','floor'].includes(nt)) n += 1;
      if (!['work','group'].includes(nt))         n += 1;
      if (nt !== 'work')                          n += 1;
      if (this.stornoMode) n += 4; else n += 3;
      return n;
    },
  },
  methods: {
    round6(v) {
      return Math.round((parseFloat(v) || 0) * 1e6) / 1e6;
    },
    onNewFactInput(row) {
      let newV = parseFloat(row.newFactValue) || 0;
      if (newV < 0) {
        newV = 0;
        row.newFactValue = '0';
      }
      if (!this.exceedAllowed) {
        const plan       = row.workSetAmount || 0;
        const cumulative = row.cumulativeFact || 0;
        if (plan > 0) {
          const maxVal = Math.max(0, this.round6(plan - cumulative));
          if (newV > maxVal) {
            newV = maxVal;
            row.newFactValue = String(this.round6(newV));
            this.$q?.notify?.({ type: 'warning', message: 'Новый факт ограничен остатком по работе' });
          }
        }
      }
      const orig = parseFloat(row.originalFactValue) || 0;
      row.stornoDiff = this.round6(newV - orig);
      if (row.materialsLoaded && (row.originalMaterials || []).length) {
        recalcStornoMaterialsByFact({
          newFactValue:      row.newFactValue,
          originalMaterials: row.originalMaterials,
          workMaterials:     row.workMaterials,
          workPlan:          row.workSetAmount,
          parseFactNum:      (v) => parseFloat(v) || 0,
          r6:                this.round6,
        });
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$q.notify({ message: 'Скопировано', color: 'positive', timeout: 1000, position: 'top' });
      }).catch(() => {});
    },
    toggleMaterials(row) {
      row.materialsOpen = !row.materialsOpen;
      if (row.materialsOpen && !row.materialsLoaded && !row.materialsLoading) {
        this.$emit('load-history-materials', row);
      }
    },
    _findOm(row, mat) {
      return (row.originalMaterials || []).find((om) => om.material_id === mat.material_id) || null;
    },
    getMatOriginal(row, mat) {
      return this._findOm(row, mat)?.originalValue ?? '0';
    },
    getMatNew(row, mat) {
      return this._findOm(row, mat)?.newValue ?? '0';
    },
    getMatDiff(row, mat) {
      const om = this._findOm(row, mat);
      if (!om) return 0;
      const n = parseFloat(om.newValue) || 0;
      const o = parseFloat(om.originalValue) || 0;
      return this.round6(n - o);
    },
    onMatInput(row, mat, val) {
      this.$emit('history-material-input', { row, mat, val });
    },
  },
};
</script>

<style scoped>
.fact-history-card {
  display: flex;
  flex-direction: column;
  max-height: 100vh;
}
.fact-history-summary {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}
.fact-history-chip {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 99px;
  background: #eff6ff;
  color: #1e40af;
  font-size: 13px;
  font-weight: 500;
}
.fact-history-chip--count {
  background: #f1f5f9;
  color: #64748b;
}
.fact-history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}
.fact-history-table th {
  position: sticky;
  top: 38px;
  z-index: 2;
  background: #f8fafc;
  padding: 8px 10px;
  font-weight: 600;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
  text-align: left;
  white-space: nowrap;
}
.fact-history-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fact-history-table tbody tr:hover {
  background: #f8fafc;
}
.fh-col-date { width: 90px; }
.fh-col-block { width: 80px; }
.fh-col-floor { width: 70px; }
.fh-col-group { width: 14%; }
.fh-col-work  { width: auto; }
.fh-col-vol   { width: 80px; }
.fh-col-smr   { width: 120px; }
.fh-col-tmc   { width: 120px; }
.fh-col-guid  { width: 280px; }
.fh-col-sent  { width: 50px; }
.fh-cell-guid {
  font-size: 11px;
  color: #64748b;
  font-family: monospace;
  white-space: nowrap;
  overflow: visible;
  text-overflow: unset;
  display: flex;
  align-items: center;
  gap: 2px;
}
.fh-guid-text {
  user-select: all;
}
.fh-guid-copy {
  opacity: 0.4;
  transition: opacity 0.15s;
}
.fh-cell-guid:hover .fh-guid-copy {
  opacity: 1;
}
.fh-filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: flex-end;
}
.fh-group-block {
  margin-bottom: 18px;
}
.fh-group-header {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #e0f2fe;
  border-radius: 6px 6px 0 0;
  margin-bottom: 0;
}
.fh-group-title {
  font-weight: 700;
  font-size: 14px;
  color: #0c4a6e;
}
.fh-group-totals {
  font-size: 12px;
  color: #475569;
}
.fh-col-plan { width: 80px; }
.fh-col-storno-new { width: 110px; }
.fh-col-storno-diff { width: 90px; }
.fh-col-storno-act { width: 50px; }
.fh-storno-cell {
  padding: 2px 4px !important;
}
.fh-storno-input {
  max-width: 100px;
}
.fh-storno-input :deep(.q-field__control) {
  min-height: 28px;
  height: 28px;
}
.fh-storno-input :deep(.q-field__native) {
  padding: 0 6px;
  font-size: 13px;
  text-align: right;
}
.fh-col-expand { width: 32px; }
.fh-expand-cell {
  padding: 2px !important;
  text-align: center;
}
.fh-mats-row > td {
  background: #fffaf0;
  padding: 8px 12px !important;
  border-bottom: 1px solid #fde68a;
}
.fh-mats-cell {
  padding: 0 !important;
}
.fh-mats-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin: 4px 0;
}
.fh-mats-table th {
  position: static;
  background: #fef3c7;
  color: #78350f;
  font-weight: 600;
  padding: 4px 8px;
  border-bottom: 1px solid #fcd34d;
  text-align: left;
  white-space: nowrap;
}
.fh-mats-table td {
  padding: 3px 8px;
  border-bottom: 1px solid #fef3c7;
  color: #334155;
  white-space: nowrap;
}
.fh-mat-new-cell {
  padding: 2px 6px !important;
  text-align: right;
  width: 110px;
}
.fh-row-unsent > td {
  background: #fff7ed;
  box-shadow: inset 3px 0 0 0 #f59e0b;
}
.fh-row-unsent:hover > td {
  background: #ffedd5;
}
.fh-mats-row.fh-row-unsent > td {
  background: #fff3e0;
  box-shadow: inset 3px 0 0 0 #f59e0b;
}
</style>
